import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { TID } from '@/constants/testIds';
import { ChevronRight, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const LOGO = '/logo.png';

export default function Login() {
  const { login, loginWithGoogle, error } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState('coach@playmasteria.com');
  const [password, setPassword] = useState('Coach#2026');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const u = await login(email, password);
      navigate(u.role === 'admin' ? '/admin' : '/dashboard');
    } catch (e) {
      setErr(error || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (resp) => {
    setErr(null);
    try {
      const u = await loginWithGoogle(resp.credential);
      navigate(u.role === 'admin' ? '/admin' : '/dashboard');
    } catch {
      setErr('No se pudo iniciar sesión con Google');
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4">
      <div className="absolute inset-0 court-grad pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6">
          <img src={LOGO} alt="PlayMaster IA" className="h-24" />
        </Link>
        <div className="bg-card-steel border-2 border-gold/40 rounded-lg p-8 shadow-2xl">
          <h1 className="font-h uppercase text-3xl mb-1">{t('welcome_back')}</h1>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-6">
            {t('login')} · Coach / Player / Admin
          </div>

          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('email')} / Usuario</label>
              <input
                data-testid={TID.loginEmail}
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('password')}</label>
              <div className="relative">
                <input
                  data-testid={TID.loginPassword}
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 pr-12 text-white outline-none transition"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gold/70 hover:text-gold"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {err && <div className="text-alert text-sm bg-alert/10 border border-alert/40 rounded p-2">{err}</div>}
            <button
              data-testid={TID.loginSubmit}
              disabled={loading}
              type="submit"
              className="mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-gold text-navy hover:bg-gold-light font-h uppercase tracking-wider font-semibold disabled:opacity-50 transition"
            >
              {loading ? '...' : t('login')} <ChevronRight className="w-4 h-4" />
            </button>
          </form>

          <div className="my-4 flex items-center gap-3" data-testid="login-google-divider">
            <div className="flex-1 h-px bg-steel" />
            <span className="text-[10px] text-gray-500 uppercase tracking-widest">{t('or')}</span>
            <div className="flex-1 h-px bg-steel" />
          </div>

          <div className="flex justify-center" data-testid="login-google-btn">
            <GoogleLogin
              onSuccess={handleGoogle}
              onError={() => setErr(t('error_generic'))}
              theme="filled_black"
              shape="rectangular"
              text="continue_with"
              locale={lang}
              width="320"
            />
          </div>

          <div className="mt-6 text-center text-sm text-gray-400">
            {t('or')} <Link to="/register" className="text-gold hover:underline ms-1">{t('create_account')}</Link>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Demo: <span className="text-gold font-mono">coach@playmasteria.com</span> / <span className="text-gold font-mono">Coach#2026</span></p>
        </div>
      </div>
    </div>
  );
}
